import { Injectable } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { APP_CONFIG } from 'app.config';
import { blobToDataURL, dataURLToBlob } from 'blob-util';
import { HttpHelper } from './http/HttpHelper';
import map from 'lodash-es/map';
import isEmpty from 'lodash-es/isEmpty';
import uniqueId from 'lodash-es/uniqueId';
import find from 'lodash-es/find';
import { saveAs } from 'file-saver';
import { catchError, from, Observable, of } from 'rxjs';

const removeBase64Header = (data: string) => {
    if (!data) {
        return data;
    }
    const base64Header = 'base64,';
    const index = data.indexOf(base64Header);
    return index > -1 ? data.substring(index + base64Header.length) : data;
};

const addImageBase64Header = (data: string) => 'data:image/jpeg;base64,' + data;

const asFileArray = (files: FileList): File[] => {
    if (!files) {
        return [];
    }

    const result: File[] = [];
    for (let i = 0; i < files.length; i++) {
        result.push(files[i]);
    }
    return result;
};

const checkFileSize = (files: File[], maxFileSize: number) => {
    return !find(files, file => file.size > maxFileSize);
};

const readDataBase64 = (file: File) => {
    return new Promise<any>((resolve: any, reject: any) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = () => {
            resolve({
                name: file.name,
                size: file.size,
                type: file.type,
                data: reader.result.toString(),
            });
        };
        reader.onerror = () => {
            reject({
                name: file.name,
                size: file.size,
                type: file.type,
                message: '',
            });
        };
    });
};

const readFiles = (files: File[]) => {
    return Promise.all(map(files, readDataBase64));
};

const getFileExt = (filename: string): string => {
    return filename.slice((filename.lastIndexOf('.') - 1 >>> 0) + 2);
};

export enum UpLoadProfileEnum {
    AVATAR = 'avatar',
    CERTIFICATE = 'certificate',
    IDENTIFICATION = 'identification',
    CURRICULUMVITAE = 'curriculumVitae',
    INTERVIEW = 'interview',
    OFFER = 'offer',
    CANDIDATE_ATTACHMENT = 'candidateAttachment',
    POSTING = 'posting',
    INTERVIEW_REPORT = 'interviewReport',
    ASSIGNMENT = 'assignment',
    OFFER_LETTER = 'offerLetter',
    PROGRAM_GIFT = 'programGift',

}

export const docCodeTypeByDmsCode = {
    [UpLoadProfileEnum.AVATAR]: 'AT002',
    [UpLoadProfileEnum.CERTIFICATE]: 'AT004',
    [UpLoadProfileEnum.IDENTIFICATION]: 'AT001',
    [UpLoadProfileEnum.CURRICULUMVITAE]: 'AT003',
    [UpLoadProfileEnum.CANDIDATE_ATTACHMENT]: 'AT005'
}

@Injectable({
    providedIn: 'root'
})
export class DmsService {
    constructor(private _httpHelper: HttpHelper, private _sanitizer: DomSanitizer) {
    }

    // getMultiPartContent(identifier, responseType: 'blob' | 'url' | 'safeUrl' = 'blob'): Promise<any> {
    //     const params = `identifier=${encodeURIComponent(identifier)}`;
    //     return this._httpHelper.methodGetMultiPart(DMS_SERVICE_URL.GET_MULTIPART_CONTENT + '?' + params)
    //         .then(resData => {
    //             if (resData && resData.errorcode) {
    //                 return undefined;
    //             }
    //             const blob = resData;
    //             if (responseType === 'blob') {
    //                 return blob;
    //             }

    //             return blobToDataURL(blob).then(dataUrl => {
    //                 if (responseType === 'safeUrl') {
    //                     return this._sanitizer.bypassSecurityTrustUrl(dataUrl);
    //                 }
    //                 return dataUrl;
    //             });
    //         });
    // }

    getMultiPartContent(identifier, typeCode: UpLoadProfileEnum = UpLoadProfileEnum.INTERVIEW, responseType: 'blob' | 'url' | 'safeUrl' = 'blob'): Promise<any> {
        const params = `identifier=${encodeURIComponent(identifier)}&typeCode=${typeCode}`;
        return this._httpHelper.methodGetMultiPart(DMS_SERVICE_URL.DOWNLOAD + '?' + params)
            .then(resData => {
                if (resData && resData.errorcode) {
                    return undefined;
                }
                const blob = resData;
                if (responseType === 'blob') {
                    return blob;
                }

                return blobToDataURL(blob).then(dataUrl => {
                    if (responseType === 'safeUrl') {
                        return this._sanitizer.bypassSecurityTrustUrl(dataUrl);
                    }
                    return dataUrl;
                });
            });
    }

    getAttachmentFromDms(url: string, identifier, responseType: 'blob' | 'url' | 'safeUrl' = 'blob'): Promise<any> {
        const params = `identifier=${encodeURIComponent(identifier)}`;
        return this._httpHelper.methodGetMultiPart(url + '?' + params)
            .then(resData => {
                if (resData && resData.errorcode) {
                    return undefined;
                }
                const blob = resData;
                if (responseType === 'blob') {
                    return blob;
                }

                return blobToDataURL(blob).then(dataUrl => {
                    if (responseType === 'safeUrl') {
                        return this._sanitizer.bypassSecurityTrustUrl(dataUrl);
                    }
                    return dataUrl;
                });
            });
    }

    getMultiPartContentDownloadDoc(identifier, typeCode: UpLoadProfileEnum = UpLoadProfileEnum.INTERVIEW, responseType: 'blob' | 'url' | 'safeUrl' = 'blob'): Promise<any> {
        const params = `identifier=${encodeURIComponent(identifier)}&typeCode=${typeCode}`;
        return this._httpHelper.methodGetMultiPart(DMS_SERVICE_URL.DOWNLOAD_DOC + '?' + params)
            .then(resData => {
                if (resData && resData.errorcode) {
                    return undefined;
                }
                const blob = resData;
                if (responseType === 'blob') {
                    return blob;
                }

                return blobToDataURL(blob).then(dataUrl => {
                    if (responseType === 'safeUrl') {
                        return this._sanitizer.bypassSecurityTrustUrl(dataUrl);
                    }
                    return dataUrl;
                });
            });
    }

    getMultiPartContentEnvelop(identifier, responseType: 'blob' | 'url' | 'safeUrl' = 'blob'): Promise<any> {
        const params = `identifier=${encodeURIComponent(identifier)}`;
        return this._httpHelper.methodGetMultiPart(DMS_SERVICE_URL.GET_MULTIPART_CONTENT_ENVELOP + '?' + params)
            .then(resData => {
                if (resData && resData.errorcode) {
                    return undefined;
                }
                const blob = resData;
                if (responseType === 'blob') {
                    return blob;
                }

                return blobToDataURL(blob).then(dataUrl => {
                    if (responseType === 'safeUrl') {
                        return this._sanitizer.bypassSecurityTrustUrl(dataUrl);
                    }
                    return dataUrl;
                });
            });
    }

    getPdfMultiPartContentEnvelop(identifier, responseType: 'blob' | 'url' | 'safeUrl' = 'blob'): Promise<any> {
        const params = `identifier=${encodeURIComponent(identifier)}`;
        return this._httpHelper.methodGetMultiPart(DMS_SERVICE_URL.GET_PDF_MULTIPART_CONTENT_ENVELOP + '?' + params)
            .then(resData => {
                if (resData && resData.errorcode) {
                    return undefined;
                }
                const blob = resData;
                if (responseType === 'blob') {
                    return blob;
                }

                return blobToDataURL(blob).then(dataUrl => {
                    if (responseType === 'safeUrl') {
                        return this._sanitizer.bypassSecurityTrustUrl(dataUrl);
                    }
                    return dataUrl;
                });
            });
    }

    getPdfDownloadAllEnvelope(envelopeAggId, responseType: 'blob' | 'url' | 'safeUrl' = 'blob'): Promise<any> {
        const params = `envelopeAggId=${encodeURIComponent(envelopeAggId)}`;
        return this._httpHelper.methodGetMultiPart(DMS_SERVICE_URL.GET_PDF_DOWNLOAD_ALL + '?' + params)
            .then(resData => {
                if (resData && resData.errorcode) {
                    return undefined;
                }
                const blob = resData;
                if (responseType === 'blob') {
                    return blob;
                }

                return blobToDataURL(blob).then(dataUrl => {
                    if (responseType === 'safeUrl') {
                        return this._sanitizer.bypassSecurityTrustUrl(dataUrl);
                    }
                    return dataUrl;
                });
            });
    }

    postMultiPartContent(formData: FormData, type: UpLoadProfileEnum = UpLoadProfileEnum.INTERVIEW, dataUpload = null) {
        if (type === UpLoadProfileEnum.OFFER_LETTER) {
            formData.append('fileType', dataUpload.fileType);
            return this._httpHelper.methodPostMultiPartCustomHeader(DMS_SERVICE_URL.UPLOAD_FILE_OFFER_LETTER, formData, dataUpload.header, dataUpload.fieldHeader);
        }
        return this._httpHelper.methodPostMultiPart(DMS_SERVICE_URL.UPLOAD_PROFILE_AVATAR + `${type}/upload`, formData);
    }

    postSingleUploadFile(file: File, type: UpLoadProfileEnum, payload?: Partial<{ username: string, tenantCode: string, appCode: string, userId: string, fullName: string, globalEmail: string }>) {
        const cmd = {
            ...payload,
            'name': file.name,
        }
        const formData = new FormData();
        formData.append('cmd', JSON.stringify(cmd));
        formData.append('file', file, file.name);
        return from(this._httpHelper.methodPostMultiPart(DMS_SERVICE_URL.UPLOAD_PROFILE_AVATAR + `${type}/upload`, formData))
            .pipe(
                catchError(err => {
                    console.log('err', err);
                    return err;
                })
            );
    }
    postSingleUploadFileNotAvatar(file: File, payload?: Partial<{ username: string, tenantCode: string, appCode: string, userId: string, fullName: string, globalEmail: string }>) {
        const cmd = {
            ...payload,
            'name': file.name,
        }
        const formData = new FormData();
        formData.append('cmd', JSON.stringify(cmd));
        formData.append('file', file, file.name);
        return from(this._httpHelper.methodPostMultiPart(DMS_SERVICE_URL.UPLOAD_FILE, formData))
            .pipe(
                catchError(err => {
                    console.log('err', err);
                    return err;
                })
            );
    }

    postMultiPartContentLargeFile(formData: FormData, fileName: string = '') {
        const cmd = {
            'fileName': fileName,
            'extension': getFileExt(fileName)
        }
        formData.append('cmd', JSON.stringify(cmd));
        return this._httpHelper.methodPostMultiPart(DMS_SERVICE_URL.UPLOAD_LARGE_FILE, formData, 600000);
    }

    postMultiPartContentEnvelop(formData: FormData, fileName: string = '') {
        formData.append('fileName', fileName);
        formData.append('extension', getFileExt(fileName));
        return this._httpHelper.methodPostMultiPart(DMS_SERVICE_URL.UPLOAD_FILE_ENVELOP, formData);
    }

    saveAsBlob(blob, fileName: string): void {
        if (blob && !isEmpty(fileName)) {
            saveAs(blob, fileName);
        }
    }

    saveAsFromBase64(base64: string, fileName: string): void {
        if (!isEmpty(base64) && !isEmpty(fileName)) {
            const blob = dataURLToBlob(base64);
            saveAs(blob, fileName);
        }
    }

    delete(identifier: string, typeCode: UpLoadProfileEnum = UpLoadProfileEnum.INTERVIEW): Promise<any> {
        const params = `?identifier=${encodeURIComponent(identifier)}&typeCode=${typeCode}`;
        return this._httpHelper.methodDeleteService(DMS_SERVICE_URL.DELETE + params);
    }

    deleteEnvelop(identifier: string): Promise<any> {
        const params = `?identifier=${encodeURIComponent(identifier)}`;
        return this._httpHelper.methodDeleteService(DMS_SERVICE_URL.DELETE_ENVELOP + params);
    }

    selectFile(accept: string = '*', maxFileSize: number = 3 * 1048576, multiple: boolean = false): Promise<any> {
        const id = uniqueId();
        const inputFile = document.createElement('input');
        inputFile.accept = accept;
        inputFile.multiple = multiple;
        inputFile.type = 'file';
        inputFile.name = id;
        inputFile.id = id;
        inputFile.setAttribute('style', 'display:none');
        const promise = new Promise<any>((resolve: any, reject: any) => {
            inputFile.onclick = () => {
                const eventName = 'mouseover';
                const useCapture = true;
                const onMouseover = () => {
                    document.body.removeEventListener(eventName, onMouseover, useCapture);
                    setTimeout(() => {
                        const files: File[] = asFileArray(inputFile.files);
                        if (!checkFileSize(files, maxFileSize)) {
                            return reject({
                                files: [],
                                message: 'ERROR_MAX_FILE_SIZE',
                            });
                        }
                        return resolve({
                            files,
                            message: null,
                        });
                    }, 100);
                };
                document.body.addEventListener(eventName, onMouseover, useCapture);
            };
            inputFile.click();
        });
        return promise;
    }

    selectAndUploadMultipartFiles(accept: string = '*', maxFileSize: number = 3 * 1048576, multiple: boolean = false): Promise<any> {
        return this.selectFile(accept, maxFileSize, multiple)
            .then(({ files }) => {
                if (files.length > 0) {
                    const arrayPromises = map(files, (file) => {
                        const formData: FormData = new FormData();
                        formData.append('file', file, file.name);
                        formData.append('cmd', JSON.stringify({
                            'name': file.name
                        }));
                        return this.postMultiPartContent(formData).then(data => {
                            if (data && data.errorcode) {
                                throw {
                                    message: data.message || 'SYSTEM_ERROR',
                                };
                            }
                            return data;
                        });
                    });
                    return Promise.all(arrayPromises);
                } else {
                    return [];
                }
            });
    }

    getResources(refCodes: Array<string>): Promise<any> {
        // const params = 'refCodes=' + JSON.stringify(refCodes);
        // return this._httpHelper.methodPostService(DMS_SERVICE_URL.DOWNLOAD_FILE, params, APP_CONFIG.QUERY_TIMEOUT);
        const params = `${JSON.stringify(refCodes)}`;
        return this._httpHelper.methodPostJsonService(DMS_SERVICE_URL.GET_RESOURCES, params);
    }

    getResourcesEnvelop(refCodes: Array<string>): Promise<any> {
        const params = `${JSON.stringify(refCodes)}`;
        return this._httpHelper.methodPostJsonService(DMS_SERVICE_URL.GET_RESOURCES_ENVELOP, params);
    }

    downloadAttachment(input: { identifier: string, type: UpLoadProfileEnum }, responseType: 'arraybuffer' | 'blob' | 'url' | 'safeUrl' = 'arraybuffer'): Observable<any> {
        const url = DMS_SERVICE_URL.GET_DOWNLOAD_ATTACHMENT;
        const params = `identifier=${encodeURIComponent(input.identifier)}&typeCode=${encodeURIComponent(input.type)}`;
        return this._httpHelper.methodDownloadAttachment(url + '?' + params);
    }

    downloadAttachmentNotAvatar(input: { identifier: string }, responseType: 'arraybuffer' | 'blob' | 'url' | 'safeUrl' = 'arraybuffer'): Observable<any> {
        const url = DMS_SERVICE_URL.DOWNLOAD_DOC;
        // console.log('input', input);

        const params = `identifier=${encodeURIComponent(input.identifier)}`;
        return this._httpHelper.methodDownloadAttachment(url + '?' + params);
    }

    downloadFileToHostMachine(data: Blob | ArrayBuffer, fileName: string, fileType: string) {
        let blob: Blob;

        if (data instanceof ArrayBuffer) {
            blob = new Blob([data], { type: fileType });
        } else {
            blob = data;
        }

        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = fileName;
        link.click();

        // It's important to revoke the object URL to avoid memory leaks
        window.URL.revokeObjectURL(url);
    }

    blobToUrl(blob: Blob): string {
        return URL.createObjectURL(blob);
    }

    getMultiPartContentImage(identifier, fileName, responseType: 'blob' | 'url' | 'safeUrl' = 'blob'): Promise<any> {
        const params = `identifer=${encodeURIComponent(identifier)}`;
        return this._httpHelper.methodGetMultiPart(DMS_SERVICE_URL.GET_MULTIPART_CONTENT + '?' + params)
            .then(resData => {
                if (resData && resData.errorcode) {
                    return undefined;
                }
                const blob = resData;
                if (responseType === 'blob') {
                    return { 'blob': blob, 'fileName': fileName, 'identifier': identifier };
                }

                return blobToDataURL(blob).then(dataUrl => {
                    if (responseType === 'safeUrl') {
                        return this._sanitizer.bypassSecurityTrustUrl(dataUrl);
                    }
                    return dataUrl;
                });
            });
    }

    getAvatarContent(identifier, responseType: 'blob' | 'url' | 'safeUrl' = 'blob'): Promise<any> {
        const params = `identifer=${encodeURIComponent(identifier)}`;
        return this._httpHelper.methodGetMultiPart(DMS_SERVICE_URL.GET_AVATAR_CONTENT + '?' + params)
            .then(resData => {
                if (resData && resData.errorcode) {
                    return undefined;
                }
                const blob = resData;
                if (responseType === 'blob') {
                    return blob;
                }

                return blobToDataURL(blob).then(dataUrl => {
                    if (responseType === 'safeUrl') {
                        return this._sanitizer.bypassSecurityTrustUrl(dataUrl);
                    }
                    return dataUrl;
                });
            });
    }

    getUserAvatar(username, responseType: 'blob' | 'url' | 'safeUrl' = 'blob'): Promise<any> {
        const params = `username=${encodeURIComponent(username)}`;
        return this._httpHelper.methodGetMultiPart(DMS_SERVICE_URL.GET_USER_AVATAR + '?' + params)
            .then(resData => {
                if (resData && (resData.errorcode || resData.type.indexOf('image') === -1)) {
                    return undefined;
                }
                const blob = resData;
                if (responseType === 'blob') {
                    return blob;
                }

                return blobToDataURL(blob).then(dataUrl => {
                    if (responseType === 'safeUrl') {
                        return this._sanitizer.bypassSecurityTrustUrl(dataUrl);
                    }
                    return dataUrl;
                });
            });
    }

    downloadMulti(identifiers, responseType: 'blob' | 'url' | 'safeUrl' = 'blob'): Promise<any> {
        let request = { "identifiers": identifiers };
        const params = `${JSON.stringify(request)}`;
        return this._httpHelper.methodPostJsonService(DMS_SERVICE_URL.DOWNLOAD_MULTI, params, APP_CONFIG.COMMAND_TIME_OUT)
            .then(resData => {
                if (resData && resData.errorcode) {
                    return undefined;
                }
                const blob = resData;
                if (responseType === 'blob') {
                    return blob;
                }

                return blobToDataURL(blob).then(dataUrl => {
                    if (responseType === 'safeUrl') {
                        return this._sanitizer.bypassSecurityTrustUrl(dataUrl);
                    }
                    return dataUrl;
                });
            });
    }

    getFileLocal(url): Promise<any> {
        return this._httpHelper.methodGetMultiPart(url)
            .then(resData => {
                const blob = resData;
                // return blobToDataURL(blob).then(dataUrl => {
                //     return this._sanitizer.bypassSecurityTrustUrl(dataUrl);
                // });
                return blob;
            });
    }

    getMultiPartContentMain({ identifier, accessToken, responseType = 'blob', fileName, isDownloadAll }: any, isPreview: boolean): Promise<any> {
        // const params = `identifier=${encodeURIComponent(identifier)}`;
        const docRegex = new RegExp('^.+\.(doc|docx)$');
        const excelRegex = new RegExp('.*\.(xlsx|xls|csv)');
        let params = '';
        // if (fileName && (docRegex.test(fileName) || excelRegex.test(fileName))) {
        //     isPreview = true;
        // }

        // if(accessToken || isDownloadAll){
        //     params = DMS_SERVICE_URL.DOWNLOAD_FILE_VNG_CLOUND;
        // } else {
        //     params = CONTRACT_URL.VIEW_FILE  + identifier;
        // }
        params = DMS_SERVICE_URL.DOWNLOAD_FILE_VNG_CLOUND;


        return this._httpHelper.methodPostMultiPartWithCustomHeader(params, { identifer: identifier, preview: isPreview, accessToken: accessToken, appCode: APP_CONFIG.APP_CODE }, responseType)
            .then(resData => {
                if (resData && resData.errorcode) {
                    return undefined;
                }
                const blob = resData;
                if (responseType === 'blob') {
                    return blob;
                }
                if (responseType === 'arraybuffer') {
                    return blob;
                }

                return blobToDataURL(blob).then(dataUrl => {
                    if (responseType === 'safeUrl') {
                        return this._sanitizer.bypassSecurityTrustUrl(dataUrl);
                    }
                    return dataUrl;
                });
            });
    }

    async getAccessTokenView(refCodes, isIncludeDoc?: boolean) {
        const params = {
            "refCodes": refCodes,
            "includePreviewDoc": true
        };

        return this._httpHelper.methodPostJsonService(DMS_SERVICE_URL.REQUEST_TOKEN, params);
    }

    getAccessTokenViewObs(refCodes, isIncludeDoc?: boolean) {
        const params = {
            "refCodes": refCodes,
            "includePreviewDoc": true
        };

        return from(this._httpHelper.methodPostJsonService(DMS_SERVICE_URL.REQUEST_TOKEN, params));
    }

    async getAccessTokenDownload(refCodes) {
        let params = {
            "refCodes": refCodes
        };
        return this._httpHelper.methodPostJsonService(DMS_SERVICE_URL.REQUEST_TOKEN, params);
    }

    getSelfieContent(identifier, responseType: 'blob' | 'url' | 'safeUrl' = 'blob'): Promise<any> {
        const params = `identifier=${encodeURIComponent(identifier)}`;
        return this._httpHelper.methodGetMultiPart(DMS_SERVICE_URL.DOWNLOAD + '?' + params)
            .then(resData => {
                if (resData && resData.errorcode) {
                    return undefined;
                }
                const blob = resData;
                if (responseType === 'blob') {
                    return { 'blob': blob, 'identifier': identifier };
                }

                return blobToDataURL(blob).then(dataUrl => {
                    if (responseType === 'safeUrl') {
                        return this._sanitizer.bypassSecurityTrustUrl(dataUrl);
                    }
                    return dataUrl;
                });
            });
    }

    getFileToView(payload:
        {
            identifier: string,
            accessToken: string,
            preview?: boolean,
            responseType: any
        },
        url: string = DMS_SERVICE_URL.DOWNLOAD_FILE_VNG_CLOUND) {
        let params = {
            identifer: payload.identifier,
            accessToken: payload.accessToken,
            preview: payload.preview,
            appCode: APP_CONFIG.APP_CODE
        }
        return this._httpHelper.http
            .post(url, params, { responseType: payload.responseType })
    }
}

export class DMS_SERVICE_URL {
    static UPLOAD_PROFILE_AVATAR = APP_CONFIG.HOST_NAME + 'services/mcrhrdms/api/profile/'
    static UPLOAD_FILE = APP_CONFIG.MCRHRDMS + 'doc/upload';
    static UPLOAD_LARGE_FILE = APP_CONFIG.MCR_BULK_UPLOAD + 'large/bulkupload';
    static GET_RESOURCES = APP_CONFIG.MCRHRDMS + 'resource/infos';
    static DOWNLOAD = APP_CONFIG.MCRHRDMS + 'profile/download';
    static DOWNLOAD_DOC = APP_CONFIG.MCRHRDMS + 'doc/download';
    static GET_MULTIPART_CONTENT = APP_CONFIG.MCRHRDMS + 'doc/info';
    static DELETE = APP_CONFIG.MCRHRDMS + 'profile/delete';

    static GET_DOWNLOAD_ATTACHMENT = APP_CONFIG.HOST_NAME + 'services/mcrhrdms/api/profile/download';
    static GET_USER_AVATAR = APP_CONFIG.HOST_NAME + 'services/mcrowldms/api/dms/avatar/userAvatar';
    static DOWNLOAD_MULTI = '';
    static GET_AVATAR_CONTENT = '';

    static UPLOAD_FILE_ENVELOP = APP_CONFIG.HOST_NAME + 'services/mcrowldms/api/envelope/upload';
    static UPLOAD_FILE_OFFER_LETTER = APP_CONFIG.MCRHRRCRT + 'pub/vf/uploadAttachment';
    static GET_MULTIPART_CONTENT_ENVELOP = APP_CONFIG.HOST_NAME + 'services/mcrowldms/api/envelope/downloadPdf';
    static GET_PDF_MULTIPART_CONTENT_ENVELOP = APP_CONFIG.HOST_NAME + 'services/mcrowldms/api/envelope/pdf/download';
    static GET_PDF_DOWNLOAD_ALL = APP_CONFIG.HOST_NAME + 'services/mcrowldms/api/envelope/pdf/downloadAll';
    static GET_RESOURCES_ENVELOP = APP_CONFIG.HOST_NAME + 'services/mcrowldms/api/envelope/resources';
    static DELETE_ENVELOP = APP_CONFIG.HOST_NAME + 'services/mcrowldms/api/envelope/resource';
    static REQUEST_TOKEN = APP_CONFIG.HOST_NAME_GW + 'sta/auth/requestToken';
    static DOWNLOAD_FILE_VNG_CLOUND = APP_CONFIG.HOST_NAME_VNG_CLOUD + 'pub/files/download';
    static GET_SELFIE_CONTENT = APP_CONFIG.DMS_RESOURCE + 'api/selfie/download';
}